/**
 * License Detail
 * Comprehensive license view with status, modules, and timeline
 */
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  ArrowLeft,
  Key,
  Building2,
  Calendar,
  Clock,
  Shield,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Ban,
  Sparkles,
  Package,
  Timer,
  Activity,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Progress,
  Input,
  useToast,
} from "@jourfx/ui";
import { SuperAdminGuard } from "../../components/SuperAdminGuard";
import { StatusBadge, type StatusType } from "../../components/StatusBadge";
import { useDateFormat } from "../../hooks/useDateFormat";

type Id<T extends string> = string & { __tableName: T };

function LicenseDetailContent() {
  const { t } = useTranslation("licenses");
  const { t: tc } = useTranslation("common");
  const { formatDate: formatLocalizedDate, formatShortDate } = useDateFormat();
  const { licenseId } = useParams({ strict: false }) as { licenseId: string };
  const navigate = useNavigate();
  const { toast } = useToast();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const license = useQuery(api.admin.licenses.get, {
    id: licenseId as Id<"licenses">,
  });

  const revokeLicense = useMutation(api.admin.licenses.revoke);
  const extendLicense = useMutation(api.admin.licenses.extend);
  const suspendLicense = useMutation(api.admin.licenses.suspend);
  const reactivateLicense = useMutation(api.admin.licenses.reactivate);

  const handleRevoke = async () => {
    setIsRevoking(true);
    try {
      await revokeLicense({ id: licenseId as Id<"licenses"> });
      toast({
        title: t("detail.revokeSuccess", "Lizenz widerrufen"),
        description: t("detail.revokeSuccessMessage", "Die Lizenz wurde erfolgreich widerrufen."),
      });
      navigate({ to: "/licenses" });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsRevoking(false);
      setRevokeDialogOpen(false);
    }
  };

  const handleRenew = () => {
    if (!licenseId) return;
    setIsExtending(true);
    extendLicense({ id: licenseId as Id<"licenses">, days: 30 })
      .then(() => {
        toast({
          title: t("actions.extend", "Lizenz verlängert"),
          description: t("actions.extendSuccess", "Die Lizenz wurde um 30 Tage verlängert."),
        });
      })
      .catch((error) => {
        toast({
          title: tc("error.title"),
          description: error instanceof Error ? error.message : tc("error.unknown"),
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsExtending(false);
      });
  };

  const handleSuspend = async () => {
    if (!licenseId) return;
    setIsSuspending(true);
    try {
      await suspendLicense({
        id: licenseId as Id<"licenses">,
        reason: suspensionReason || undefined,
      });
      toast({
        title: t("actions.suspend", "Lizenz ausgesetzt"),
        description: t("actions.suspendSuccess", "Die Lizenz wurde ausgesetzt."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSuspending(false);
      setSuspendDialogOpen(false);
      setSuspensionReason("");
    }
  };

  const handleReactivate = async () => {
    if (!licenseId) return;
    setIsReactivating(true);
    try {
      await reactivateLicense({ id: licenseId as Id<"licenses"> });
      toast({
        title: t("actions.reactivate", "Lizenz reaktiviert"),
        description: t("actions.reactivateSuccess", "Die Lizenz ist wieder aktiv."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsReactivating(false);
      setReactivateDialogOpen(false);
    }
  };

  // Loading state
  if (license === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 rounded bg-muted animate-pulse" />
            <div className="h-4 w-32 rounded bg-muted animate-pulse" />
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-24 rounded bg-muted animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Not found
  if (license === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-warning/20 blur-xl" />
          <AlertTriangle className="relative h-16 w-16 text-warning" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          {t("detail.notFound", "Lizenz nicht gefunden")}
        </h2>
        <Link
          to="/licenses"
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {tc("actions.back")}
        </Link>
      </div>
    );
  }

  const now = Date.now();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const isV2 = license.v === 2;
  const isExpired = license.validUntil < now;
  const isExpiring = !isExpired && license.validUntil - now < thirtyDays;
  const daysRemaining = Math.max(0, Math.ceil((license.validUntil - now) / (24 * 60 * 60 * 1000)));
  const totalDays = Math.ceil((license.validUntil - license.validFrom) / (24 * 60 * 60 * 1000));
  const daysUsed = totalDays - daysRemaining;
  const usagePercent = Math.min(100, Math.max(0, (daysUsed / totalDays) * 100));

  // Tier styling
  const tierConfig: Record<string, { gradient: string; icon: typeof Sparkles; label: string }> = {
    enterprise: {
      gradient: "from-primary/70 to-primary",
      icon: Sparkles,
      label: t("tiers.enterprise"),
    },
    professional: {
      gradient: "from-info/70 to-info",
      icon: Shield,
      label: t("tiers.professional"),
    },
    basic: {
      gradient: "from-muted to-muted-foreground/60",
      icon: Package,
      label: t("tiers.basic"),
    },
  };

  const tier = tierConfig[license.tier || "basic"] || tierConfig.basic;
  const TierIcon = tier.icon;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/licenses"
            className="rounded-lg p-2 transition-colors hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </Link>
          <div className="flex items-center gap-4">
            {/* Tier badge with gradient */}
            <div className={`relative flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${tier.gradient} shadow-lg`}>
              <TierIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-foreground">
                  {license.customerName || t("table.unknown")}
                </h1>
                <StatusBadge status={license.status as StatusType} />
              </div>
              <p className="text-muted-foreground">{license.customerId}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {license.status === "active" && (
            <>
              {isV2 ? (
                <Button variant="outline" onClick={() => navigate({ to: "/licenses" })}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("actions.replaceToken", "Load replacement token")}
                </Button>
              ) : (
                <Button variant="outline" onClick={handleRenew} disabled={isExtending}>
                  {isExtending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {t("actions.extend", "Verlängern")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setSuspendDialogOpen(true)}>
                <Ban className="mr-2 h-4 w-4" />
                {t("actions.suspend", "Aussetzen")}
              </Button>
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                onClick={() => setRevokeDialogOpen(true)}
              >
                <Ban className="mr-2 h-4 w-4" />
                {t("detail.revoke", "Widerrufen")}
              </Button>
            </>
          )}
          {(license.status === "suspended" || license.status === "revoked") && (
            <Button variant="outline" onClick={() => setReactivateDialogOpen(true)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t("actions.reactivate", "Reaktivieren")}
            </Button>
          )}
        </div>
      </div>

      {isV2 && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="font-medium text-foreground">
            {t("detail.v2ReplacementTitle", "Replace this v2 token from the licenses page")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              "detail.v2ReplacementDescription",
              "V2 licenses are signed tokens and cannot be extended in place. Load a replacement token from the licenses page instead."
            )}
          </p>
        </div>
      )}

      {/* Expiry warning */}
      {isExpiring && !isExpired && (
        <div className="flex items-center gap-3 rounded-lg border border-warning-border bg-warning-muted p-4">
          <AlertTriangle className="h-5 w-5 text-warning" />
          <div>
            <p className="font-medium text-warning-muted-foreground">
              {t("detail.expiringWarning", "Lizenz läuft bald ab")}
            </p>
            <p className="text-sm text-warning-muted-foreground/80">
              {t("detail.expiringDays", { days: daysRemaining })}
            </p>
          </div>
        </div>
      )}

      {isExpired && (
        <div className="flex items-center gap-3 rounded-lg border border-error-border bg-error-muted p-4">
          <XCircle className="h-5 w-5 text-error" />
          <div>
            <p className="font-medium text-error-muted-foreground">
              {t("detail.expiredWarning", "Lizenz abgelaufen")}
            </p>
            <p className="text-sm text-error-muted-foreground/80">
              {t("detail.expiredOn", { date: formatLocalizedDate(license.validUntil) })}
            </p>
          </div>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* License validity card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5 text-muted-foreground" />
              {t("detail.validity", "Gültigkeit")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress bar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("detail.timeUsed", "Verbraucht")}
                </span>
                <span className="font-medium tabular-nums">
                  {daysUsed} / {totalDays} {t("detail.days", "Tage")}
                </span>
              </div>
              <Progress
                value={usagePercent}
                className={`h-3 ${
                  isExpired
                    ? "[&>div]:bg-error"
                    : isExpiring
                      ? "[&>div]:bg-warning"
                      : "[&>div]:bg-success"
                }`}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatShortDate(license.validFrom)}</span>
                <span>{formatShortDate(license.validUntil)}</span>
              </div>
            </div>

            {/* Date details */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {t("detail.validFrom", "Gültig ab")}
                </div>
                <p className="mt-1 font-semibold">{formatLocalizedDate(license.validFrom)}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {t("detail.validUntil", "Gültig bis")}
                </div>
                <p className={`mt-1 font-semibold ${isExpired ? "text-error" : isExpiring ? "text-warning" : ""}`}>
                  {formatLocalizedDate(license.validUntil)}
                </p>
              </div>
            </div>

            {/* Days remaining highlight */}
            {!isExpired && (
              <div className={`rounded-lg p-4 text-center ${
                isExpiring
                  ? "bg-warning-muted"
                  : "bg-success-muted"
              }`}>
                <p className={`text-4xl font-bold tabular-nums ${
                  isExpiring
                    ? "text-warning-muted-foreground"
                    : "text-success-muted-foreground"
                }`}>
                  {daysRemaining}
                </p>
                <p className={`text-sm ${
                  isExpiring
                    ? "text-warning"
                    : "text-success"
                }`}>
                  {t("detail.daysRemaining", "Tage verbleibend")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-muted-foreground" />
              {t("detail.tier", "Lizenz-Stufe")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`rounded-xl bg-gradient-to-br ${tier.gradient} p-6 text-white shadow-lg`}>
              <TierIcon className="h-8 w-8 mb-3" />
              <p className="text-2xl font-bold">{tier.label}</p>
              <p className="mt-1 text-white/80 text-sm">
                {license.kind === "base"
                  ? t("detail.baseLicense", "Basis-Lizenz")
                  : t("detail.moduleLicense", "Modul-Lizenz")}
              </p>
            </div>

            {/* Modules */}
            {license.modules && license.modules.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium text-muted-foreground mb-3">
                  {t("detail.includedModules", "Enthaltene Module")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {license.modules.map((module: string) => (
                    <Badge
                      key={module}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <CheckCircle className="h-3 w-3" />
                      {module}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            {t("detail.activity", "Aktivität")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("detail.customer", "Kunde")}
                </p>
                <p className="font-medium">{license.customerId}</p>
              </div>
            </div>

            {license.activatedAt && (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-muted">
                  <CheckCircle className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("detail.activatedAt", "Aktiviert am")}
                  </p>
                  <p className="font-medium">{formatShortDate(license.activatedAt)}</p>
                </div>
              </div>
            )}

            {license.revokedAt && (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-error-muted">
                  <Ban className="h-5 w-5 text-error" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("detail.revokedAt", "Widerrufen am")}
                  </p>
                  <p className="font-medium">{formatShortDate(license.revokedAt)}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Revoke confirmation dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("detail.revokeTitle", "Lizenz widerrufen?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("detail.revokeDescription", "Diese Aktion kann nicht rückgängig gemacht werden. Die Lizenz wird sofort ungültig und der Kunde verliert den Zugriff.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={isRevoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRevoking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("detail.revokeConfirm", "Ja, widerrufen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend confirmation dialog */}
      <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("detail.suspendTitle", "Lizenz aussetzen?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "detail.suspendDescription",
                "Die Lizenz wird vorübergehend deaktiviert. Der Kunde verliert den Zugriff, bis die Lizenz reaktiviert wird."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t("detail.suspendReason", "Grund (optional)")}
            </label>
            <Input
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              placeholder={t("detail.suspendReasonPlaceholder", "z.B. Zahlungsrückstand")}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSuspending}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspend}
              disabled={isSuspending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSuspending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("detail.suspendConfirm", "Ja, aussetzen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate confirmation dialog */}
      <AlertDialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("detail.reactivateTitle", "Lizenz reaktivieren?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("detail.reactivateDescription", "Die Lizenz wird wieder aktiviert, sofern sie nicht abgelaufen ist.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReactivating}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate} disabled={isReactivating}>
              {isReactivating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("detail.reactivateConfirm", "Ja, reaktivieren")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function LicenseDetail() {
  return (
    <SuperAdminGuard>
      <LicenseDetailContent />
    </SuperAdminGuard>
  );
}
